/**
 * `ScheduledRunRow` — one SCHEDULER-started run that never produced a
 * harness task (`reminder` / `system` kinds), sourced from the execution
 * log via `schedule.runs` (recjobs handoff §3.5(b)).
 *
 * These rows are TERMINAL by construction — the log only records a run
 * once it finished — so unlike mission and harness rows there is no live
 * progress, no expand chevron, and no cancel affordance. That is the
 * honest shape of the data, not a missing feature.
 *
 * An UNBOUND job (no `caseId`) is shown in every view and badged
 * "sem caso", so it stays visible without ever implying it belongs to the
 * case currently on screen. See the scoping rationale in
 * `useScheduledRunsPoll`.
 */
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, MinusCircle } from "lucide-react";

import { cn } from "@/lib/utils";
import { formatClock, formatElapsed, joinMeta } from "./row-time";
import { ScheduledBadge } from "./ScheduledBadge";
import { StatusBullet } from "./StatusBullet";
import { CaseChip } from "./CaseChip";
import type { ScheduledRunTreeNode } from "./monitor-tree";

export function ScheduledRunRow({
  node,
  caseLabel,
  activeCaseId,
  caseRegistryLoaded,
}: {
  node: ScheduledRunTreeNode;
  /** Resolved case title for `node.caseId`, when the registry knows it. */
  caseLabel?: string;
  /** The case currently on screen — drives the chip's emphasis. */
  activeCaseId?: string | null;
  /** False until the case registry has loaded — gates the unresolved
   *  diagnostic so it cannot fire on first paint. */
  caseRegistryLoaded?: boolean;
}) {
  const { t, i18n } = useTranslation();

  const statusLabel = t(`rightSidebar.scheduledRunStatus.${node.status}`, {
    defaultValue: node.status,
  });

  const meta = joinMeta(
    formatClock(node.at, i18n.language),
    node.durationMs !== undefined ? formatElapsed(node.durationMs) : undefined,
  );

  // The log's own summary is the run's essence; on error the message is
  // more useful than any generic copy we could invent.
  const detail = node.status === "error" ? (node.error ?? node.summary) : node.summary;

  return (
    <li
      data-testid={`scheduled-run-${node.runId}`}
      data-status={node.status}
      title={`${node.title} — ${statusLabel}`}
      className="border-b border-cream-200/60 last:border-b-0 dark:border-coffee-800/60"
    >
      <div className="flex w-full items-start gap-2 px-3 py-2 text-left text-xs">
        <span className="inline-block w-3 shrink-0" aria-hidden />
        <ScheduledRunGlyph status={node.status} />
        <div className="min-w-0 flex-1">
          {/* The title owns the FULL first line. Badges used to sit beside
              it and squeezed the one piece of text that identifies the run
              into a ~40% column, so "Lexiun — verificação de e-mail" read
              as "Lexiun — verificação…". They now live on the meta line
              below, where the row has spare horizontal space. */}
          <div className="line-clamp-2 text-[11px] font-normal leading-snug text-slate-500 dark:text-slate-400">
            {node.title}
          </div>
          {detail && (
            <div
              className={cn(
                "mt-0.5 line-clamp-2 text-[11px] font-light leading-snug",
                node.status === "error"
                  ? "text-red-500/90 dark:text-red-400/90"
                  : "text-slate-600 dark:text-slate-300",
              )}
              title={detail}
            >
              {detail}
            </div>
          )}
          {/* Badges + timings share one line beneath the title: both are
              secondary to "what ran", and stacking them on separate rows
              would nearly double the height of every row in a list that
              routinely runs to dozens of entries. */}
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <ScheduledBadge testId={`scheduled-run-badge-${node.runId}`} />
            {node.caseId && (
              <CaseChip
                testId={`scheduled-run-case-${node.runId}`}
                caseId={node.caseId}
                label={caseLabel}
                isActiveCase={node.caseId === activeCaseId}
                registryLoaded={caseRegistryLoaded}
              />
            )}
            {/* No case → nothing shown. `system` jobs (mailbox check,
                pending sweep) are defined with no scope in the catalog,
                so having none is their normal state. */}
            {meta && (
              <span
                data-testid={`scheduled-run-meta-${node.runId}`}
                className="text-[10px] font-light tabular-nums tracking-wide text-slate-400 dark:text-slate-500"
              >
                {meta}
              </span>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function ScheduledRunGlyph({ status }: { status: ScheduledRunTreeNode["status"] }) {
  switch (status) {
    case "error":
      return <StatusBullet status="failed" />;
    case "skipped":
      // Skipped is NOT a failure — a job can be legitimately skipped by its
      // own preconditions.
      return <StatusBullet status="canceled" />;
    default:
      return <StatusBullet status="done" />;
  }
}
